import React from 'react';
import {useTranslation} from 'react-i18next';
import {Controller, useFormContext} from 'react-hook-form';
import debounce from 'lodash.debounce';
import {SelectOptionType} from '../../../utils/types';
import {EXEMPTIONS_IDS, NONE_EXEMPTION_OPTION} from 'utils/constants';
import {FORM_NAMES_GUEST_TAX} from '../TouristTaxesSetup/TouristTaxesSetup';
import {Statuses, useStatus} from 'utils/hooks';
import {usePaymentSettings} from '../../../hooks';
import {getIsAgeValid, MAX_AGE, MIN_AGE} from '../useTaxes';
import Select from 'components/acommon/FieldSelect';
import {Total} from '../../PaymentsDetailsForm/components/Summary';
import {CardRow, InputAge, TaxesSelectWrapper, Wrapper} from './styled';

const DEBOUNCE_TIMEOUT_S = 1;

type CalculateGuestTax = {
  age: string;
  exemption: string;
};

type TouristTaxesCardProps = {
  guestItemName: string;
  index: number;
  taxExemptionsOptions: SelectOptionType[];
  calculateGuestsTax: () => Promise<void>;
  className?: string;
  visibleTotal?: boolean;
  setTotalStatus?: (status: Statuses) => void;
};

function TouristTaxesCard({
  guestItemName,
  visibleTotal,
  setTotalStatus,
  className,
  taxExemptionsOptions,
  calculateGuestsTax,
}: TouristTaxesCardProps) {
  const {t, i18n} = useTranslation();
  const {isLoading, setStatus: setLocalStatus} = useStatus();
  // const {ErrorModal} = useErrorModal();
  const {paymentSettingsCurrencySign: currency, paymentSettingsCurrency: currencyLabel} =
    usePaymentSettings();
  const {
    register,
    watch,
    setValue,
    control,
    trigger,
    getValues,
    formState: {isDirty, isSubmitted},
  } = useFormContext();

  // const {
  //   reservation: {id: reservationId = null},
  //   isFranceReservation,
  // } = useComputedReservationDetails();

  // const {calculateAllGuestTax} = useTaxes({onError: displayError});

  const ageName = `${guestItemName}.${FORM_NAMES_GUEST_TAX.age}` as const;
  const exemptionName = `${guestItemName}.${FORM_NAMES_GUEST_TAX.exemption}` as const;
  const totalName = `${guestItemName}.${FORM_NAMES_GUEST_TAX.total}` as const;
  const age = watch(ageName);
  const exemption = watch(exemptionName);
  const totalPrice = watch(totalName) || 0;

  register(totalName);

  // const getGuestTaxCalculationPayload = React.useCallback(
  //   (age: string, exemption: string) => {
  //     return {
  //       reservation_id: reservationId,
  //       guest_list: [{age, exemption}],
  //       calculate_only: true,
  //     };
  //   },
  //   [reservationId],
  // );

  const startLoading = React.useCallback(() => {
    setTotalStatus?.(Statuses.loading);
    setLocalStatus(Statuses.loading);
  }, [setLocalStatus, setTotalStatus]);

  const stopLoading = React.useCallback(() => {
    setTotalStatus?.(Statuses.idle);
    setLocalStatus(Statuses.idle);
  }, [setLocalStatus, setTotalStatus]);

  const calculateGuestTaxes = React.useMemo(
    () =>
      debounce(
        async ({
          age = '0',
          exemption = NONE_EXEMPTION_OPTION.value,
        }: CalculateGuestTax) => {
          // const payload = getGuestTaxCalculationPayload(age, exemption);
          // const {data: tax, error} = await api.seasonGuests.post(payload);
          //
          // if (!isMounted.current) {
          //   return;
          // }
          //
          // stopLoading();
          //
          // if (error) displayError(error);
          //
          // setValue(totalName, tax?.amount || 0);

          await calculateGuestsTax();
          stopLoading();
        },
        DEBOUNCE_TIMEOUT_S * 1000,
      ),
    [calculateGuestsTax, stopLoading],
  );

  const startCalculateGuestTax = React.useCallback(
    (props: CalculateGuestTax) => {
      startLoading();
      return calculateGuestTaxes(props);
    },
    [calculateGuestTaxes, startLoading],
  );

  const validateAndCalculateTaxesData = React.useCallback(
    ({age = '0', exemption = NONE_EXEMPTION_OPTION.value}: CalculateGuestTax) => {
      if (getIsAgeValid(age)) {
        startCalculateGuestTax({age, exemption});
      } else {
        setValue(totalName, undefined);
      }
    },
    [startCalculateGuestTax, setValue, totalName],
  );

  const handlerChangingExemptions = React.useCallback(
    (age: string) => {
      const selectedExemptionValue = exemption?.value;
      taxExemptionsOptions.forEach(exemption => {
        const exemptionValue = exemption?.value;
        switch (exemptionValue) {
          case EXEMPTIONS_IDS.under14:
            if (Number(age) >= 14 && selectedExemptionValue === exemptionValue) {
              setValue(exemptionName, NONE_EXEMPTION_OPTION);
            } else if (Number(age) < 14) {
              setValue(exemptionName, exemption);
            }
            break;
          case EXEMPTIONS_IDS.under18:
            if (Number(age) >= 18 && selectedExemptionValue === exemptionValue) {
              setValue(exemptionName, NONE_EXEMPTION_OPTION);
            } else if (Number(age) < 18) {
              setValue(exemptionName, exemption);
            }
        }
      });
    },
    [taxExemptionsOptions, setValue, exemptionName, exemption?.value],
  );

  const handleChangeAge = React.useCallback(
    (onChange: (event: React.ChangeEvent<HTMLInputElement>) => void) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event?.target?.value;
        handlerChangingExemptions(value);

        validateAndCalculateTaxesData({
          age: value,
          exemption: getValues(exemptionName)?.value,
        });

        return onChange(event);
      },
    [exemptionName, getValues, handlerChangingExemptions, validateAndCalculateTaxesData],
  );

  const handleChangeExemption = React.useCallback(
    (onChange: (event: SelectOptionType) => void) => (event: SelectOptionType) => {
      const value = event?.value;

      validateAndCalculateTaxesData({
        age,
        exemption: String(value),
      });

      return onChange(event);
    },
    [age, validateAndCalculateTaxesData],
  );

  const ageRules = {
    min: {
      value: MIN_AGE,
      message: t('min_number_is_short', {number: MIN_AGE}),
    },
    max: {
      value: MAX_AGE,
      message: t('max_number_is_short', {number: MAX_AGE}),
    },
  };

  React.useLayoutEffect(
    function handleGenderOptionsLanguage() {
      const nextExemptionOption = taxExemptionsOptions?.find(option => {
        return option.value === exemption?.value;
      });
      setValue(exemptionName, nextExemptionOption);
    },
    [exemption?.value, exemptionName, i18n.language, setValue, taxExemptionsOptions],
  );

  React.useEffect(() => {
    if (isDirty || isSubmitted) trigger();
  }, [i18n.language, isDirty, isSubmitted, trigger]);

  return (
    <Wrapper className={className}>
      <CardRow>
        <Controller
          name={ageName}
          rules={ageRules}
          render={({field: {onChange, ...restField}, fieldState: {error}}) => {
            return (
              <InputAge
                onChange={handleChangeAge(onChange)}
                label={t('age')}
                placeholder="--"
                type="number"
                error={error?.message}
                {...restField}
              />
            );
          }}
        />

        <TaxesSelectWrapper>
          <Controller
            name={exemptionName}
            control={control}
            render={({field: {onChange, ...restField}, fieldState: {error}}) => {
              return (
                <Select
                  label={t('exemption')}
                  options={taxExemptionsOptions}
                  onChange={handleChangeExemption(onChange)}
                  error={error?.message}
                  {...restField}
                />
              );
            }}
          />
        </TaxesSelectWrapper>
      </CardRow>
      {visibleTotal && (
        <Total
          currency={currency}
          currencyLabel={currencyLabel}
          isLoading={isLoading}
          title={t('total_taxes')}
          price={totalPrice.toFixed(2)}
        />
      )}
      {/*<ErrorModal />*/}
    </Wrapper>
  );
}

export {TouristTaxesCard};
